import { useCallback, useEffect, useRef, useState } from "react";
import Hero from "../components/Hero";
import FilterBar, { DEFAULT_FILTER_VALUES, toAppliedFilters } from "../components/FilterBar";
import AdsBanner from "../components/AdsBanner";
import PropertySection from "../components/PropertySection";
import AIAdvisorSection from "../components/AIAdvisorSection";
import PropifyAcademyAd from "../components/PropifyAcademyAd";
import Seo from "../components/Seo";
import { searchVentures } from "../services/ventureApi";
import { PAGE_SEO } from "../seo/site";

function HomePage({ onAdvisorClick }) {
  const [filterValues, setFilterValues] = useState(DEFAULT_FILTER_VALUES);
  const [appliedFilters, setAppliedFilters] = useState(() => toAppliedFilters(DEFAULT_FILTER_VALUES));
  const [selectedReraNo, setSelectedReraNo] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isSuggestionsLoading, setIsSuggestionsLoading] = useState(false);
  const [suggestionsError, setSuggestionsError] = useState("");
  const suggestionRequestId = useRef(0);

  useEffect(() => {
    const query = searchInput.trim();
    const requestId = ++suggestionRequestId.current;

    setSuggestions([]);
    setSuggestionsError("");

    if (!query) {
      setIsSuggestionsLoading(false);
      return undefined;
    }

    setIsSuggestionsLoading(true);
    const debounceTimer = setTimeout(async () => {
      try {
        const result = await searchVentures({
          query,
          pageNumber: 1,
          pageSize: 5
        });

        if (requestId !== suggestionRequestId.current) {
          return;
        }

        const nextSuggestions = result.items.map((item) => item?.ProjectName || item?.projectName || "").filter(Boolean);
        setSuggestions(Array.from(new Set(nextSuggestions)).slice(0, 5));
      } catch (error) {
        if (requestId !== suggestionRequestId.current) {
          return;
        }

        setSuggestionsError(error?.message || "Unable to load matching ventures.");
      } finally {
        if (requestId === suggestionRequestId.current) {
          setIsSuggestionsLoading(false);
        }
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchInput]);

  const handleSearchSubmit = useCallback((value) => {
    const nextQuery = value.trim();
    const normalizedRera = nextQuery.replace(/\s+/g, "");
    const isReraSearch = /^P\d{5,}$/i.test(normalizedRera);

    setSearchInput(value);
    setSearchQuery(nextQuery);
    setSelectedReraNo(isReraSearch ? normalizedRera : "");
  }, []);

  return <>
      <Seo {...PAGE_SEO.home} />
      <Hero
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        onSearchSubmit={handleSearchSubmit}
        suggestions={suggestions}
        isSuggestionsLoading={isSuggestionsLoading}
        suggestionsError={suggestionsError}
      />

      <FilterBar
        values={filterValues}
        onChange={setFilterValues}
        onApply={setAppliedFilters}
      />

      <div className="vd-listings-layout">
        <div className="vd-listings-main">
          <PropertySection
            builderName={appliedFilters.builderName || "All Builders"}
            reraNo={selectedReraNo}
            searchQuery={searchQuery}
            filters={appliedFilters}
          />
        </div>
        <AdsBanner />
      </div>

      <section className="vd-advisor-section">
        <div className="row g-3">
          <div className="col-12 col-lg-8">
            <AIAdvisorSection onAdvisorClick={onAdvisorClick} />
          </div>
          <div className="col-12 col-lg-4">
            <PropifyAcademyAd />
          </div>
        </div>
      </section>
    </>;
}

export default HomePage;
